<template>
  <div ref="elementRef" class="relative grid h-full" :class="[roundedCornersClasses, { 'overflow-hidden': parallaxLayer }]">
    <div v-style="{ pb: parallaxOffset && contentPane }" class="h-full grid-area-stack">
      <div ref="gradientAreaRef" class="h-full flex items-center overflow-hidden" :class="{ 'absolute-0': parallaxOffset }">
        <div ref="parallaxRef" class="w-full" :style="{ height: parallaxOffset ? `calc(100% + ${parallaxTranslate})` : '100%' }">
          <base-picture
            v-if="responsiveMedia.images"
            v-bind="responsiveMedia.images"
            class="full"
            :fetchpriority
            fit="cover"
            :lazy
            :props-img="{ class: 'h-full' }"
          />
          <vf-video
            v-else-if="responsiveMedia.video"
            v-bind="responsiveMedia.video"
            ref="videoRef"
            :autoplay="!lazy"
            class="full"
            fit="cover"
            loop
            :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
          />
          <cms-shared-hot-zones v-if="hasHotZones" :media />
          <base-link
            v-if="mediaLink && !hasHotZones"
            aria-hidden="true"
            class="absolute-0"
            :data-cta="mediaLink.delocalizedTracking"
            tabindex="-1"
            :target="mediaLink.targetAttribute"
            :to="mediaLink.url"
            @click="handlePromotionClick('img')"
          />
          <cms-base-gradient
            v-if="gradientStyle && gradientStyle !== 'off'"
            v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
            :alignment="gradientAlignment[contentAlignment]"
            :position="gradientPosition[contentAlignment]"
            :text-area-only-extent
          />
        </div>
      </div>
    </div>

    <cms-base-parallax-layer class="pointer-events-none relative grid-area-stack" :config="parallaxLayer">
      <div class="h-full f-col" :class="alignment[contentAlignment]">
        <div
          ref="textAreaRef"
          v-style:bg="backdrop"
          class="relative f-col overflow-hidden"
          :class="[
            variantContentClass[variant] || variantContentClass.default,
            spacing,
            blurClass,
          ]"
        >
          <h2
            v-if="title"
            v-style:c="titleColor"
            class="pointer-within"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-banner-title"
          >
            <base-link
              v-if="mediaLink"
              :data-cta="mediaLink.delocalizedTracking"
              :target="mediaLink.targetAttribute"
              :to="mediaLink.url"
              @click="handlePromotionClick('title')"
            >
              <cms-plain-text :content field-name="title" />
            </base-link>
            <template v-else>
              <cms-plain-text :content field-name="title" />
            </template>
          </h2>
          <p
            v-if="subtitle"
            v-style:c="subtitleColor"
            :class="subtitleStyle"
            data-test-id="cms-banner-text"
          >
            <cms-plain-text :content field-name="subtitle" />
          </p>
          <cms-shared-cta-group
            v-if="targets.length"
            :class="[buttons, { 'justify-center': centerButtonAlignment.includes(contentAlignment) }]"
            :cta-type
            :equal-target-size
            :multilink-style
            :multilink-title
            :size
            :targets
            :text-link-style="getTextLinkStyles(content)"
            @click="handleCtaClick(`cta${$event + 1}`)"
          />
          <cms-rich-text
            v-if="richTextBelowTargets"
            v-style:c="richTextBelowTargetsColor || subtitleColor"
            class="pointer-within"
            :content="{ richText: richTextBelowTargets }"
          />
        </div>
      </div>
    </cms-base-parallax-layer>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { Responsive } from '#types/common'
import type { BannerEspotAlignment, BannerEspotContent } from '#types/components/cms/banner-espot'
import type { ButtonSize } from '#types/components/vf/button'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{
  content: BannerEspotContent
}>()

const {
  backdrop,
  backdropBlur,
  contentAlignment,
  ctaType,
  equalTargetSize,
  gradientBreakpoints,
  gradientGrid,
  gradientStyle,
  gradientTone,
  hideTitle,
  media,
  mediaTarget,
  multilinkStyle,
  multilinkTitle,
  name,
  parallax,
  promotionTracking,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  subtitle,
  subtitleColor,
  subtitleStyle,
  targets = [],
  title,
  titleColor,
  titleStyle,
  variant,
} = content

const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)
const { path } = useRoute()
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)
const { $gtm } = useNuxtApp()
const appConfig = useAppConfig()
const cms = appConfig.components.cms
const { enableTextAreaOnlyGradient } = cms.gradient
const {
  brandClasses: {
    container: {
      spacing,
      variants: variantContentClass
    },
    buttons
  }
} = cms.bannerEspot
const { componentCorners } = cms.section
const isTextAreaOnlyGradient = isTextAreaOnlyGradientEnabled(enableTextAreaOnlyGradient, gradientStyle)

const isPlpPage = getUrlId(path, 'Category')
const handleCtaClick = (ctaIndex: string) => {
  handlePromotionClick(ctaIndex)
  // Track PLP In-Grid Banner CTA if on PLP page
  if (isPlpPage) {
    $gtm.push('plpPage.onPlpInGridBannerCta', name)
  }
}

const videoRef = ref()
const gradientAreaRef = useTemplateRef('gradientAreaRef')
const textAreaRef = useTemplateRef('textAreaRef')
const responsiveMedia = getResponsiveMedia(media)
const aspectRatio = responsiveMedia?.aspectRatio || {}
const size = { sm: { size: 'tiny' as ButtonSize } }
const { height: gradientAreaHeight } = useElementSize(gradientAreaRef)
const { height: textAreaHeight } = useElementSize(textAreaRef)
const { top: gradientAreaTop } = useElementBounding(gradientAreaRef)
const { top: textAreaTop } = useElementBounding(textAreaRef)

const mediaLink = mediaTarget || targets[0]
const hasHotZones = computed(() => Object.values(media || []).some((media) => media.enableImageMap))

const contentPane = Object.keys(aspectRatio).reduce((acc, cur) => {
  acc[cur] = `calc(1 / ${aspectRatio[cur]} * 100%)`
  return acc
}, {})

const parallaxOffset = parallax?.config.innerOffset
const parallaxLayer = useParallaxTextLayer(parallax?.config.textLayer)
const parallaxRef = useTemplateRef('parallaxRef')
const { config: parallaxConfig, translate: parallaxTranslate } = getOffsetParallaxConfig(parallaxOffset, {
  0: 0,
  1: 30,
  2: 40,
  3: 50,
  4: 75,
  5: 100,
})

if (parallaxConfig) {
  useParallaxController(parallaxRef, parallaxConfig)
}

const alignment: Partial<Record<BannerEspotAlignment, string>> = {
  'bottom': 'text-center justify-end items-center pb-8',
  'center': 'text-center center',
  'left': 'justify-center items-start',
  'top': 'text-center items-center',
  'bottom-left': 'justify-end items-start'
}

const centerButtonAlignment = ['bottom', 'center', 'top']

const gradientPosition: Record<BannerEspotAlignment, 'top' | 'bottom' | 'center'> = {
  'bottom': 'bottom',
  'center': 'center',
  'left': 'center',
  'top': 'top',
  'bottom-left': 'bottom'
}

const gradientAlignment: Record<BannerEspotAlignment, 'left' | 'right' | 'center'> = {
  'bottom': 'center',
  'center': 'center',
  'left': 'left',
  'top': 'center',
  'bottom-left': 'left'
}

const textAreaOnlyExtent = computed<Responsive<string> | undefined>(() => {
  if (!isTextAreaOnlyGradient)
    return undefined

  const position = gradientPosition[contentAlignment]
  const fallbackExtent = position === 'center' ? '67%' : '33%'
  if (!textAreaHeight.value)
    return { sm: fallbackExtent, md: fallbackExtent, lg: fallbackExtent }

  const extent = position === 'bottom'
    ? gradientAreaHeight.value && textAreaTop.value && gradientAreaTop.value
      ? gradientAreaHeight.value - (textAreaTop.value - gradientAreaTop.value)
      : textAreaHeight.value
    : position === 'center'
      ? gradientAreaHeight.value
        ? (gradientAreaHeight.value + textAreaHeight.value) / 2
        : undefined
      : textAreaHeight.value

  const resolvedExtent = extent && extent > 0
    ? `${Math.ceil(extent)}px`
    : fallbackExtent

  return {
    sm: resolvedExtent,
    md: resolvedExtent,
    lg: resolvedExtent,
  }
})

const roundedCornersClasses = getRoundedCorners(content, componentCorners, { force: true })

useAutoplay(videoRef)

const blurClass = backdropBlur && (backdropBlur === 'low' ? 'backdrop-blur-xs' : 'backdrop-blur-md')
</script>
